/**
 * Substrate Modifier Constants
 *
 * Substrate affects market perception (ceiling), not production cost (floor).
 * Applied as a multiplicative factor in the ceiling equation only.
 * Source: P12 remediation.
 */

import type { MediumKey } from '@/types';
import { MEDIUMS } from '@/types/medium';

export interface SubstrateConfig {
  modifier: number;
  label: string;
  default?: boolean;
}

export type SubstrateGroup = 'canvas' | 'paper';

export const SUBSTRATE_MODIFIERS: Record<SubstrateGroup, Record<string, SubstrateConfig>> = {
  canvas: {
    stretched_canvas: { modifier: 1.00, label: 'Stretched Canvas', default: true },
    canvas_panel:     { modifier: 0.90, label: 'Canvas Panel' },
    wood_panel:       { modifier: 1.10, label: 'Wood Panel (Birch/Cradled)' },
    linen:            { modifier: 1.15, label: 'Linen' },
    paper:            { modifier: 0.75, label: 'Paper / Cardboard' },
  },
  paper: {
    cotton_rag:       { modifier: 1.00, label: 'Cotton Rag Paper', default: true },
    yupo_synthetic:   { modifier: 0.95, label: 'Yupo / Synthetic' },
    board_panel:      { modifier: 1.10, label: 'Board / Panel' },
  },
};

/**
 * Get the substrate group for a medium.
 */
export function getSubstrateGroup(medium: MediumKey): SubstrateGroup {
  return MEDIUMS[medium]?.isPaperBased ? 'paper' : 'canvas';
}

/**
 * Get the default substrate key for a medium's group.
 */
export function getDefaultSubstrate(medium: MediumKey): string {
  const group = getSubstrateGroup(medium);
  const entries = Object.entries(SUBSTRATE_MODIFIERS[group]);
  const defaultEntry = entries.find(([, config]) => config.default);
  return defaultEntry ? defaultEntry[0] : entries[0][0];
}

/**
 * Get the substrate modifier value. Returns 1.0 for invalid/missing keys.
 */
export function getSubstrateModifier(medium: MediumKey, substrate?: string): number {
  const group = getSubstrateGroup(medium);
  const key = substrate ?? getDefaultSubstrate(medium);
  return SUBSTRATE_MODIFIERS[group][key]?.modifier ?? 1.0;
}

/**
 * Get available substrate options for a medium.
 */
export function getSubstrateOptions(medium: MediumKey): Array<{ key: string; label: string; modifier: number; isDefault: boolean }> {
  const group = getSubstrateGroup(medium);
  return Object.entries(SUBSTRATE_MODIFIERS[group]).map(([key, config]) => ({
    key,
    label: config.label,
    modifier: config.modifier,
    isDefault: !!config.default,
  }));
}
